/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState, FilterNames, FilterRestService,
  FormStateBuilder,
  ListActionPipelines,
} from '@gh/builder-shared';
import { BuilderPermissionSet } from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { Message } from '@gh/core-messages';
import { newFormControl } from '@gh/core-ui';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { beforeCurrentDate } from '@gh/core-util';
import { PriceBookListBusinessService } from '../../resources/price-book-list-business.service';
import { PriceBookListItem } from '../../resources/price-book.model';
import { PriceBookGridDefinition } from './price-book-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-price-book-list',
  templateUrl: 'price-book-list.component.html',
  viewProviders: [PriceBookGridDefinition],
})
export class PriceBookListComponent implements OnInit {

  dataSource: SlicedDataSource<PriceBookListItem>;
  cursor = newGridPaginationCursor();
  form: FilterableFormState<any>;
  filter: any;
  showWithdrawn = newFormControl(false);

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private routeData: RouteDataService,
              private router: AppRouter,
              private filterService: FilterRestService,
              private formStateBuilder: FormStateBuilder,
              public gridDefinition: PriceBookGridDefinition,
              public navigationService: BuilderNavigationService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private businessService: PriceBookListBusinessService) {
  }

  ngOnInit(): void {
    const { filter: initFilter, gridLayout } = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition.bind(gridLayout);
    this.form = filterableForm(
      this.formStateBuilder.buildPlain({
                                         value: initFilter,
                                         children: {
                                           showExpired:  void 0,
                                           showActive: void 0,
                                         },
                                       }));
    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = filter;
      this.dataSource = this.businessService.findAllAsDataSource(filter);
      if (!isInit) {
        this.filterService.update(FilterNames.priceBooks, filter).subscribe();
      }
      isInit = false;
    });
  }

  onCreate(): void {
    this.router.navigateToChild(['/estimating/price-books/new']);
  }

  onEdit(priceBook: PriceBookListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Estimating.PriceBook.EDIT_VIEW)) {
      this.router.navigateToChild(['/estimating/price-books', priceBook.priceBookId]);
    }
  }

  onDelete(priceBook: PriceBookListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        notificationUsed: Message.warning('msg_pb_delete_used'),
        confirmNotUsed: {
          id: 'msg_pb_delete',
          params: {
            name: priceBook.name,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.remove(priceBook, state.value)),
      },
      finish: () => this.dataSource = this.businessService.findAllAsDataSource(this.filter),
    });

    flow.run({
      value: priceBook,
    });

  }

  onClone(priceBook: PriceBookListItem): void {
    this.router.navigateToChild(['/estimating/price-books/new'], { queryParams: { cloneFrom: priceBook.priceBookId } });
  }

  isWithdrawn(priceBook: PriceBookListItem): boolean {
    return beforeCurrentDate(priceBook.withdrawalDate);
  }

  goToAel(priceBook?: PriceBookListItem): void {
    this.navigationService.goToAel({
      entityId: priceBook ? priceBook.priceBookId : void 0,
      entityType: 'PB',
      label: 'lbl_price_books',
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }
}
